const defaults = {
  stopPropagation: false,
  placeholder: 'Type to filter',
  wrapperClass: 'select-ui-wrapper',
  detailsClass: 'select-ui-details',
  summaryClass: 'select-ui-summary',
  inputClass: 'select-ui-input',
  listClass: 'select-ui-list',
  listItemClass: 'select-ui-list-item',
  listItemSelectedClass: 'is-selected',
  errorClass: 'select-ui-error',
  errorText: 'No items found',
  markTag: 'mark',
};

const KEYS = {
  UP: 38,
  DOWN: 40,
  TAB: 9,
  ENTER: 13,
  SPACE: 32,
  SHIFT: 16,
  ESCAPE: 27,
};

const ELEMENT_SCROLL_INTO_VIEW_SUPPORT = !!Element.prototype.scrollIntoView;
const ELEMENT_SCROLL_INTO_VIEW_IF_NEEDED_SUPPORT = !!Element.prototype.scrollIntoViewIfNeeded;

/**
 * @class Select - Select UI class
 */
export class Select {
  /**
   * @param {HTMLSelectElement} select - select element
   * @param {Object} [settings=defaults] - settings object
   */
  constructor(select, settings) {
    if (!select || !(select instanceof HTMLSelectElement)) {
      throw new Error('select is required for initialization');
    }

    this.settings = Object.assign({}, defaults, settings);

    this.select = select;
    this.keyMap = new Map();

    this.wrapper = document.createElement('div');
    this.wrapper.id = `${this.select.id}-select-ui-wrapper`;
    this.wrapper.classList.add(this.settings.wrapperClass);

    this.details = document.createElement('details');
    this.summary = document.createElement('summary');

    this.details.classList.add(this.settings.detailsClass);
    this.summary.classList.add(this.settings.summaryClass);
    this.summary.setAttribute('aria-label', 'select');

    this.input = document.createElement('input');
    this.input.id = `select-ui-input--${this.select.id}`;
    this.input.type = 'search';
    this.input.autocomplete = 'off';
    this.input.required = this.select.required;
    this.input.placeholder = this.settings.placeholder;
    this.input.classList.add(this.settings.inputClass);

    this.list = document.createElement('ul');
    this.list.id = `${this.select.id}-list`;
    this.list.classList.add(this.settings.listClass);
    this.list.setAttribute('role', 'listbox');

    this.error = document.createElement('div');
    this.error.hidden = true;
    this.error.textContent = this.settings.errorText;
    this.error.classList.add(this.settings.errorClass);

    // create list items
    this.createItems();

    this.wrapper.append(this.details);
    this.details.append(this.summary);
    this.summary.append(this.input);
    this.details.append(this.list);
    this.wrapper.append(this.error);
    this.select.before(this.wrapper);

    // reconfigure labels pointing to the native select to the search input
    Array.from(document.querySelectorAll(`label[for="${this.select.id}"]`)).forEach(label => label.setAttribute('for', this.input.id));

    this.cursor = -1; // index on visible items
    this.selectedIndex = -1; // selected item index

    // used for raf debouce
    this.scheduled_animation_frame = null;
    this.scrollToItemIfNeededDebounced = this.debounce(this.scrollToItemIfNeeded);

    // hide the native select
    this.hideSelect();

    this.input.addEventListener('blur', () => this.onBlur());
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('click', () => this.open());
    this.input.addEventListener('input', event => this.onInput(event));
    this.input.addEventListener('keydown', event => this.onKeyDown(event));
    this.input.addEventListener('keyup', event => this.onKeyUp(event));

    this.summary.addEventListener('keyup', event => this.onSpace(event));
    this.details.addEventListener('toggle', event => this.onToggle(event));

    // prevent breaking <shift> + <tab> behaviour
    // when presed key combination is pressed from the input
    // we end up with an infinite loop and focus is boncing back and forth within the summary and input
    this.summary.addEventListener('focus', event => event.relatedTarget !== this.input && this.input.focus())

    // delegated item click handler
    this.list.addEventListener('click', event => this.onItemClick(event));

    document.addEventListener('click', event => this.closeOnClick(event));
    document.addEventListener('keydown', event => this.closeOnEscape(event));

    // watch native select for changes in dom
    this.observe();

    // select change handler
    this.select.addEventListener('change', event => this.onSelectChange(event));
  }

  /**
   * @private
   * Creates and set the list items from the select options
   */
  createItems() {
    const fragment = new DocumentFragment();

    Array.from(this.select.options).forEach((option, index) => {
      const li = document.createElement('li');

      li.dataset.index = index;
      li.dataset.value = option.hasAttribute('value') ? option.value : '';

      if (option.disabled) {
        li.dataset.disabled = '';
      }

      li.textContent = option.textContent;
      li.setAttribute('role', 'option');
      li.classList.add(this.settings.listItemClass);

      fragment.append(li);
    });

    this.list.innerHTML = '';
    this.list.append(fragment);

    this.items = Array.from(this.list.children);
    this.textItems = this.items.map(item => item.textContent);
    this.visibleItems = this.items.filter(item => !item.hidden);
  }

  /**
   * @public
   * open <details> menu
   */
  open() {
    if (this.details.open) {
      return;
    }

    // clear previous filter and show all items
    this.resetFilterIfNeeded();

    // open the <details>
    this.details.open = true;
  }

  /**
   * @public
   * close <details> menu
   */
  close() {
    if (!this.details.open) {
      return;
    }

    if (!this.isValid()) {
      this.reset();
    }

    this.details.open = false;
  }

  /**
   * @private
   * close <details> menu on ESCAPE
   * @param { Event } event
   */
  closeOnEscape(event) {
    const { keyCode } = event;

    if (keyCode === KEYS.ESCAPE) {
      this.close();
    }
  }

  /**
   * @private
   * close <details> menu on outside click
   * @param { Event } event
   */
  closeOnClick(event) {
    const { target } = event;

    if (target === this.summary) {
      return;
    }

    if (this.wrapper.contains(target)) {
      return;
    }

    this.close();
  }

  /**
   * @private
   * on input focus
   */
  onFocus() {
    // reset visible items if input is empty
    if (!this.input.value.trim()) {
      this.visibleItems = this.items.filter(item => !item.hidden);
    }

    this.open();
    this.toggleErrorIfNeeded();
  }

  /**
   * @private
   * do not reset the state here, because it will be visible for the user
   * do not close <details> on blur event, because the click event on items will not fire after that
   */
  onBlur() {
    this.toggleErrorIfNeeded();
  }

  /**
   * @private
   * @param {Event} event
   * change internal state on type
   */
  onInput(event) {
    const value = event.target.value.trim().toLowerCase();

    if (!value) {
      this.reset();
    }

    this.open();

    this.textItems.forEach((item, index) => this.items[index].hidden = !item.toLowerCase().includes(value));
    this.visibleItems = this.items.filter(item => !item.hidden);

    // if we have selected visible item set the cursor to it, otherwise -1 if not found
    this.cursor = this.visibleItems.findIndex(item => item === this.items[this.selectedIndex]);

    // reset marks
    this.unsetMarks();

    // set marks
    this.setMarks();

    this.toggleErrorIfNeeded();
  }

  /**
   * @private
   * @param {Event} event
   * modify keys registry - so we can detect <shift> + <tab> or other key combinations
   */
  onKeyDown(event) {
    const { key, keyCode } = event;
    const { UP, DOWN, TAB, ENTER, ESCAPE } = KEYS;

    this.keyMap.set(key, true);

    switch (keyCode) {
      case UP: {
        this.onArrowPress('up');
        event.preventDefault();
        break;
      }
      case DOWN: {
        this.onArrowPress('down');
        event.preventDefault();
        break;
      }
      case ENTER: {
        this.onEnterPress();
        event.preventDefault();
        break;
      }
      case TAB: {
        this.onTabPress();
        break;
      }
      case ESCAPE: {
        this.close();
        break;
      }
      default: {
        break;
      }
    }
  }

  /**
   * @private
   * modify keys registry
   */
  onKeyUp(event) {
    const { key } = event;

    if (this.keyMap.has(key)) {
      this.keyMap.set(key, false);
    }
  }

  /**
   * @private
   * @param {String} direction - "up" or "down" string
   */
  onArrowPress(direction) {
    if (!(direction === 'up' || direction === 'down')) {
      throw new Error('direction "up" or "down" is required');
    }

    const min = 0;
    const max = this.visibleItems.length - 1; // visible items count

    if (direction === 'up') this.cursor--;
    if (direction === 'down') this.cursor++;

    if (this.cursor < min) this.cursor = max;
    if (this.cursor > max) this.cursor = min;

    if (this.cursor >= min && this.cursor <= max) {
      this.selectItem(Number(this.visibleItems[this.cursor].dataset.index));
    }
  }

  /**
   * @private
   * On <enter> keydown handler
   */
  onEnterPress() {
    if (this.visibleItems.length === 1) {
      this.selectItem(this.visibleItems[0].dataset.index);
      this.close();
      return;
    }

    if (this.selectedIndex > -1) {
      this.close();
      return;
    }
  }

  /**
   * @private
   * On <tab> keydown handler - select first visible item on <tab>
   */
  onTabPress() {
    const vcount = this.visibleItems.length;

    if (vcount > 0) {
      if (vcount === 1 || this.cursor === -1) {
        this.selectItem(Number(this.visibleItems[0].dataset.index));
      }

      if (this.cursor > -1) {
        this.selectItem(Number(this.visibleItems[this.cursor].dataset.index));
      }
    }

    this.close();
  }

  /**
   * @private
   * summary <space> handler
   * do not close details element if input is focused and <space> is pressed
   */
  onSpace(event) {
    // we care about <space> only
    if (event.keyCode !== KEYS.SPACE) {
      return;
    }

    // do nothing if input is not focused
    if (document.activeElement !== this.input) {
      return;
    }

    // otherwise do not close the <details> element
    event.preventDefault();
  }

  /**
   * @private
   * <details> toggle handler
   * focus input on details open
   */
  onToggle() {
    if (this.details.open) {
      this.input.focus();
    }
  }

  /**
   * @private
   * On item click
   * @param {Event} event
   */
  onItemClick(event) {
    let { target } = event; // <li> list item or <mark> or settings.markTag

    // if we click on the mark tag, get the parent <li> element
    if (target.nodeName.toLowerCase() === this.settings.markTag.toLowerCase()) {
      target = target.parentNode; // <li> element
    }

    this.selectItem(Number(target.dataset.index));
    this.close();
  }

  /**
   * @public
   * Select item by index
   * @param {Number} index - select opiton index
   * @param {Boolean} [notify=true] - dispatch change event
   */
  selectItem(index, notify = true) {
    if (index < 0 || index >= this.items.length) {
      return;
    }

    const selectedItem = this.items[index];

    this.items.forEach(item => {
      item.removeAttribute('aria-selected');
      item.classList.remove(this.settings.listItemSelectedClass)
    });

    selectedItem.setAttribute('aria-selected', true);
    selectedItem.classList.add(this.settings.listItemSelectedClass);

    this.selectedIndex = index;
    this.select.selectedIndex = index;
    this.input.value = selectedItem.textContent;

    if (notify) {
      this.select.dispatchEvent(new Event('change'));
    }

    // if we have selected visible item set the cursor to it, otherwise -1 if not found
    this.cursor = this.visibleItems.findIndex(item => item === selectedItem);

    // show selected item in view if needed
    this.scrollToItemIfNeededDebounced(index);
  }

  /**
   * @public
   * Resets the component
   */
  reset() {
    this.input.value = '';

    this.cursor = -1;
    this.selectedIndex = -1;

    this.select.selectedIndex = -1;
    this.select.dispatchEvent(new Event('change'));

    this.unsetMarks();

    this.items.forEach(item => {
      item.hidden = false;
      item.removeAttribute('aria-selected');
      item.classList.remove(this.settings.listItemSelectedClass)
    });
  }

  /**
   * @public
   * Check validity of the input
   * @return {Boolean} result
   */
  isValid() {
    const text = this.input.value;

    if (this.selectedIndex === -1) {
      return false;
    }

    if (text.toLowerCase() !== this.items[this.selectedIndex].textContent.toLowerCase()) {
      return false;
    }

    return true;
  }

  /**
   * @public
   * show error message
   */
  showError() {
    this.error.hidden = false;
  }

  /**
   * @public
   * hide error message
   */
  hideError() {
    this.error.hidden = true;
  }

  /**
   * @public
   * show or hide the error message when needed
   */
  toggleErrorIfNeeded() {
    this.visibleItems.length === 0 ? this.showError() : this.hideError();
  }

  /**
   * @private
   * mark search matches
   */
  setMarks() {
    const tag = this.settings.markTag;
    const value = this.input.value.trim().toLowerCase();

    if (!value) {
      return;
    }

    this.visibleItems.forEach(item => {
      const text = item.textContent;
      const regex = new RegExp(value, 'gi');
      const html = text.replaceAll(regex, `<${tag}>$&</${tag}>`);
      item.innerHTML = html;
    });
  }

  /**
   * @private
   * reset search marks
   */
  unsetMarks() {
    this.items.forEach((item, index) => {
      const textItem = this.textItems[index];

      if (item.innerHTML !== textItem) {
        item.innerHTML = textItem;
      }
    });
  }

  /**
   * @private
   * Watch native select for change add/remove options
   * If we have added or removed options in the native select menu - we should recreate the items in the ui list
   */
  observe() {
    this.observer = new MutationObserver(this.observerHandler.bind(this));
    this.observer.observe(this.select, { childList: true, subtree: false, attributes: false });
  }

  /**
   * @private
   * MutationObserver callback function
   * If we have added or removed options in the native select menu - we should recreate the items in the ui list
   */
  observerHandler() {
    this.reset();
    this.createItems();
  }

  /**
   * @private
   * Watch native select for outside changes
   */
  onSelectChange() {
    const { selectedIndex } = this.select;
    const hasRecords = !!this.observer.takeRecords().length;

    if (hasRecords) {
      this.observerHandler();
    }

    if (selectedIndex > -1) {
      this.selectItem(selectedIndex, hasRecords);
    }
  }

  /**
   * @public
   * Clear/Reset the search/filter if any
   * This enables to show all items on next open even if search/filter is previously performed
   */
  resetFilterIfNeeded() {
    const isNeeded = this.items.length !== this.visibleItems.length;

    if (!isNeeded) {
      return;
    }

    // remove marks
    this.unsetMarks();

    // show all items
    this.items.forEach(item => item.hidden = false);
    this.visibleItems = this.items.slice();

    // set cursor and scroll to selected item
    if (this.selectedIndex > -1) {
      this.selectItem(this.selectedIndex, false);
    }
  }

  /**
   * @private
   * Scroll to item if needed
   * @param {Number} index - the index of the item to scroll to
   */
  scrollToItemIfNeeded(index) {
    // out of bound
    if (index < 0 || index >= this.items.length) {
      return;
    }

    // no need to scroll if details is closed
    if (!this.details.open) {
      return;
    }

    // no need to scroll if input is not focused
    if (this.input !== document.activeElement) {
      return;
    }

    if (ELEMENT_SCROLL_INTO_VIEW_IF_NEEDED_SUPPORT) {
      this.input.scrollIntoViewIfNeeded(true)
      this.items[index].scrollIntoViewIfNeeded(true);
    }
    else if (ELEMENT_SCROLL_INTO_VIEW_SUPPORT) {
      this.input.scrollIntoView({ block: 'center' });
      this.items[index].scrollIntoView({ block: 'center' });
    }
  }

  /**
   * @private
   * Debounce handler implementation with raf
   * @param {Function} fn - function to debounce
   * @return {Function} dfn - debounced bound to this function
   */
  debounce(fn) {
    return (...args) => {
      if (this.scheduled_animation_frame) {
        cancelAnimationFrame(this.scheduled_animation_frame);
      }
      this.scheduled_animation_frame = requestAnimationFrame(fn.bind(this, ...args));
    }
  }

  /**
   * @private
   * hides original select
   * prevent "An invalid form control with name='my_select' is not focusable." error
   * do not set "hidden" - it will prevent focus
   * do not set "disabled" - it will prevent the form field to be submitted
   * do not remove "required" - it will mess up with form validation
   */
  hideSelect() {
    this.select.style.width = 0;
    this.select.style.height = 0;
    this.select.style.margin = 0;
    this.select.style.padding = 0;
    this.select.style.opacity = 0;
    this.select.style.fontSize = 0;
    this.select.style.border = 'none';
    this.select.style.position = 'absolute';
    this.select.style.transform = 'scale(0)';
  }
}

